import { useEffect, useRef, useState } from 'react'
import { Copy, ExternalLink, QrCode } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface OutputBarProps {
  url: string | null
}

export function OutputBar({ url }: OutputBarProps) {
  const [qrOpen, setQrOpen] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [qrPng, setQrPng] = useState<string | null>(null)

  useEffect(() => {
    if (!qrOpen || !url) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear when dialog closes
      setQrPng(null)
      return
    }
    // QRCodeCanvas paints in its own useEffect; defer one frame so the canvas
    // backing store is populated before we serialise it.
    let cancelled = false
    const raf = requestAnimationFrame(() => {
      if (cancelled) return
      const canvas = canvasRef.current
      if (canvas) setQrPng(canvas.toDataURL('image/png'))
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [qrOpen, url])

  if (!url) {
    return null
  }

  async function handleCopy() {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Copy failed')
    }
  }

  return (
    <div className="space-y-2 border-t bg-background px-5 py-3">
      <output
        aria-label="Tracking URL"
        className="block max-h-32 overflow-y-auto rounded-md border border-input bg-transparent px-2.5 py-1.5 font-mono text-xs break-all select-all"
      >
        {url}
      </output>
      <div className="flex items-center justify-end gap-0.5">
        <IconButton label="Copy" onClick={handleCopy} icon={<Copy className="size-4" />} />
        <IconButton
          label="QR Code"
          onClick={() => setQrOpen(true)}
          icon={<QrCode className="size-4" />}
        />
        <Button
          asChild
          variant="ghost"
          size="icon"
          aria-label="Open in new tab"
          title="Open in new tab"
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="size-4" aria-hidden />
          </a>
        </Button>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="rounded-md border bg-white p-4">
              <QRCodeCanvas
                ref={canvasRef}
                value={url}
                size={224}
                level={url.length > 700 ? 'L' : 'M'}
                style={{ display: 'none' }}
              />
              {qrPng ? (
                <img
                  src={qrPng}
                  width={224}
                  height={224}
                  alt="QR code for tracking URL"
                  className="block"
                />
              ) : (
                <div style={{ width: 224, height: 224 }} />
              )}
            </div>
            {url.length > 700 && (
              <p className="text-center text-xs text-muted-foreground">
                URL is long ({url.length} chars) — the QR may be dense and harder to scan.
              </p>
            )}
            <p className="w-full break-all text-center font-mono text-xs text-muted-foreground">
              {url}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface IconButtonProps {
  label: string
  onClick: () => void
  icon: React.ReactNode
}

function IconButton({ label, onClick, icon }: IconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label={label} onClick={onClick}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  )
}
